/* RESPONSIBLE TEAM: team-ai-agent-2 */

import type AppService from 'embercom/services/app-service';
import Service, { inject as service } from '@ember/service';
import { dropTask } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import {
  GroupSummary,
  type GroupSummaryWireFormat,
} from 'embercom/lib/fin-playground/group-summary';
import {
  type QuestionGenerationUpdateEvent,
  type ExtractionType,
} from 'embercom/lib/fin-playground/question-generation';
import type IntlService from 'ember-intl/services/intl';
import { postRequest, request, putRequest, deleteRequest } from 'embercom/lib/inbox/requests';
import csvParser, { type ParseResult } from 'papaparse';
import { Promise as EmberPromise } from 'rsvp';
import { PlaygroundSummary } from 'embercom/lib/fin-playground/playground-summary';
import { AsyncData } from 'embercom/resources/utils/async-data';
import { use } from 'ember-resources/util/function-resource';
import type UserSummary from 'embercom/objects/inbox/user-summary';
import { tracked } from '@glimmer/tracking';
import { Playground } from 'embercom/lib/fin-playground/playground';
import { type PlaygroundQuestion } from 'embercom/lib/fin-playground/question';
import type InboxSearchSuggestionsService from './inbox-search-suggestions-service';

export default class PlaygroundApiService extends Service {
  @service declare appService: AppService;
  @service declare intercomEventService: $TSFixMe;
  @service declare notificationsService: $TSFixMe;
  @service declare intl: IntlService;
  @service declare inboxSearchSuggestionsService: InboxSearchSuggestionsService;

  @tracked playgroundLoadingFailed = false;

  // These are "lazy" variables, they are only updated when the playground settings are persisted in the database
  @tracked impersonatedUser: UserSummary | null = null;
  @tracked selectedContentSegmentIds: Array<string> = [];
  @tracked usingAudienceTargeting = false;

  unprocessedAnswerGenerationResponses: PlaygroundQuestion[] = [];

  get playgroundSummary() {
    return this.playgroundSummaryLoader.value;
  }

  get playground() {
    return this.playgroundLoader.value;
  }

  get playgroundIsLoading() {
    return this.playgroundLoader.isLoading;
  }

  get consentedToAiProcessing(): boolean {
    return this.appService.app.hasConsentedToExternalAi;
  }

  get isPlaygroundLocked() {
    return !this.consentedToAiProcessing || this.playgroundLoadingFailed;
  }

  get hasDisconnectedStandalonePlatform(): boolean {
    if (!this.appService.app.canUseStandalone) {
      return false; // For non-standalone apps, platform support is not relevant
    }

    return !this.appService.mainStandalonePlatform.isConnected;
  }

  get hasUnsupportedStandalonePlatforms(): boolean {
    if (!this.appService.app.canUseStandalone) {
      return false; // For non-standalone apps, platform support is not relevant
    }

    // If it's standalone but not Zendesk or Salesforce, it's unsupported for question generation
    return !this.appService.mainStandalonePlatform.supportsFinPlayground;
  }

  async parseCsvFile(file: any): Promise<ParseResult<any>> {
    // the CSV parser uses callbacks, so this is just a shim so we can use it as a Promise
    return new EmberPromise((resolve, reject) => {
      let complete = (results: any) => {
        if (results.errors.length) {
          // An UndetectableDelimiter happens with single column CSV files (because no line contains a ,)
          if (results.errors.any((error: any) => error.code !== 'UndetectableDelimiter')) {
            reject(results);
          }
        }
        resolve(results);
      };
      csvParser.parse(file, { complete, error: reject });
    });
  }

  async reloadPlaygroundSummary() {
    this.playgroundSummaryLoader.reload();
    return this.playgroundSummaryLoader.promise;
  }

  async reloadPlayground(groupId: number) {
    this.playgroundLoader.reload(groupId);
    return this.playgroundLoader.promise;
  }

  async deletePlaygroundGroup(groupId: number) {
    await deleteRequest(`/ember/fin_playground_group/${groupId}?app_id=${this.appService.app.id}`);

    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'delete_group_button',
      place: 'playground',
      section: 'test',
    });
  }

  async createPlaygroundGroup(
    groupName: string,
    selectedContentSegments: string[],
    brandId?: string,
    extractionType?: ExtractionType,
    questions?: string[],
    topicId?: string,
  ) {
    let createdGroupSummary = await taskFor(this._createPlaygroundGroupTask).perform(
      groupName,
      selectedContentSegments,
      brandId,
      extractionType,
      questions,
      topicId,
    );

    return createdGroupSummary;
  }

  updateQuestionGenerationSummary(data: QuestionGenerationUpdateEvent) {
    if (this.playgroundSummary) {
      this.playgroundSummary.questionGenerationSummary.updateLatestJobData(data);
    }
  }

  async updatePlaygroundGroup(groupId: number, groupName: string) {
    let updatedGroupSummary = await taskFor(this._updatePlaygroundGroupTask).perform(
      groupId,
      groupName,
    );

    if (this.playgroundSummary) {
      let indexOfUpdatedGroup = this.playgroundSummary.groupSummaries.findIndex(
        (group) => group.id === groupId,
      );

      if (indexOfUpdatedGroup !== -1) {
        // Create a new array to trigger reactivity
        let updatedGroups = [...this.playgroundSummary.groupSummaries];
        updatedGroups[indexOfUpdatedGroup] = updatedGroupSummary;

        this.playgroundSummaryLoader.update({
          ...this.playgroundSummary,
          groupSummaries: updatedGroups,
        });
      }
    }

    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'update_group_button',
      place: 'playground',
      section: 'test',
    });

    return updatedGroupSummary;
  }

  async acknowledgeQuestionGeneration(groupId?: number) {
    let response = await postRequest(
      `/ember/fin_playground/acknowledge_question_generation_job?app_id=${this.appService.app.id}`,
      {
        fin_playground_group_id: groupId,
      },
    );

    return response;
  }

  async cancelQuestionGeneration(groupId?: number) {
    await postRequest(
      `/ember/fin_playground/cancel_question_generation?app_id=${this.appService.app.id}`,
      {
        fin_playground_group_id: groupId,
      },
    );
  }

  async validateAdminPermissions() {
    await request(
      `/ember/fin_playground/validate_admin_permissions?app_id=${this.appService.app.id}`,
    );
  }

  async loadImpersonatedUser(userId?: string | null) {
    if (userId) {
      this.impersonatedUser = await this.inboxSearchSuggestionsService.loadUserFromId(userId);
    } else {
      this.impersonatedUser = null;
    }
  }

  setSelectedContentSegmentIds(selectedContentSegmentIds: Array<string>) {
    this.selectedContentSegmentIds = selectedContentSegmentIds;
  }

  setUsingAudienceTargeting(usingAudienceTargeting: boolean) {
    this.usingAudienceTargeting = usingAudienceTargeting;
  }

  @use playgroundSummaryLoader = AsyncData<PlaygroundSummary>(async () => {
    let response = await request(`/ember/fin_playground_group?app_id=${this.appService.app.id}`);
    let json = await response.json();
    return PlaygroundSummary.deserialize(json);
  });

  @use private playgroundLoader = AsyncData<Playground>(async ({ args }) => {
    let groupId = args[0];

    if (!groupId) {
      return null;
    }

    try {
      let response = await request(
        `/ember/fin_playground_group/${groupId}?app_id=${this.appService.app.id}`,
      );

      let playground = Playground.deserialize(await response.json());

      // This is necessary because while the playground is loading, we receive answer generation events from Nexus
      // that aren't processed until the playground is loaded
      // We keep track of these events in `unprocessedAnswerGenerationResponses` and process them once the playground is loaded
      playground.replaceRunningQuestionsWithCompleted(this.unprocessedAnswerGenerationResponses);
      this.unprocessedAnswerGenerationResponses = [];

      await this.loadImpersonatedUser(playground.settings.userId);
      this.selectedContentSegmentIds = playground.settings.selectedContentSegmentIds;
      this.usingAudienceTargeting = playground.settings.usingAudienceTargeting;

      this.playgroundLoadingFailed = false;
      return playground;
    } catch (error) {
      this.playgroundLoadingFailed = true;

      this.notificationsService.notifyErrorWithButton(
        this.intl.t('ai-agent.playground.call-error.first-load.message'),
        {
          label: this.intl.t('ai-agent.playground.call-error.first-load.action'),
          action: () => this.playgroundLoader.reload(),
        },
        -1,
      );
      return;
    }
  });

  @dropTask
  *_createPlaygroundGroupTask(
    groupName: string,
    selectedContentSegments: string[],
    brandId?: string,
    extractionType?: ExtractionType,
    questions?: string[],
    topicId?: string,
  ) {
    let response = (yield postRequest(
      `/ember/fin_playground_group?app_id=${this.appService.app.id}`,
      {
        group_name: groupName,
        selected_content_segments: selectedContentSegments ?? [],
        brand_id: brandId ?? null,
        extraction_type: extractionType ?? null,
        topic_id: topicId ?? null,
        questions: questions
          ?.filter((q) => q.trim()) // Exclude empty or whitespace-only questions
          .map((q) => ({ question_text: q })), // Map remaining questions to objects
      },
    )) as Response;

    let json = (yield response.json()) as GroupSummaryWireFormat;
    return GroupSummary.deserialize(json);
  }

  @dropTask
  *_updatePlaygroundGroupTask(groupId: number, groupName: string) {
    let response = (yield putRequest(
      `/ember/fin_playground_group/${groupId}?app_id=${this.appService.app.id}`,
      {
        group_name: groupName,
      },
    )) as Response;

    let json = (yield response.json()) as GroupSummaryWireFormat;
    return GroupSummary.deserialize(json);
  }

  @dropTask
  *_deletePlaygroundGroupTask(groupId: number) {
    yield deleteRequest(`/ember/fin_playground_group/${groupId}?app_id=${this.appService.app.id}`);
  }

  updateGroupSummaryFromRealTimeEvent(
    groupId: number,
    lastModifiedAt: string,
    lastModifiedByAdminId: number,
  ) {
    let currentSummary = this.playgroundSummary;
    if (!currentSummary) {
      return;
    }

    let updatedGroupSummaries = currentSummary.groupSummaries.map((group) =>
      group.id === groupId
        ? new GroupSummary(group.id, group.name, lastModifiedAt, lastModifiedByAdminId)
        : group,
    );

    // Use AsyncData.update() to trigger reactivity automatically
    this.playgroundSummaryLoader.update({
      ...currentSummary,
      groupSummaries: updatedGroupSummaries,
    });
  }
}

declare module '@ember/service' {
  interface Registry {
    'playground-api': PlaygroundApiService;
  }
}
